/**
 * Admin sidebar navigation with permission-based menu visibility.
 *
 * @module components/admin/AdminSidebar
 * @see TASK-FRONTEND-ADMIN-SHELL-CHG-003
 */
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import SpeedIcon from "@mui/icons-material/Speed";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import StorageIcon from "@mui/icons-material/Storage";
import BusinessIcon from "@mui/icons-material/Business";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import PeopleIcon from "@mui/icons-material/People";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import CloudIcon from "@mui/icons-material/Cloud";
import PsychologyIcon from "@mui/icons-material/Psychology";
import HubIcon from "@mui/icons-material/Hub";
import MemoryIcon from "@mui/icons-material/Memory";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ChatIcon from "@mui/icons-material/Chat";
import SecurityIcon from "@mui/icons-material/Security";
import ScienceIcon from "@mui/icons-material/Science";
import SmsIcon from "@mui/icons-material/Sms";
import FlagIcon from "@mui/icons-material/Flag";
import SettingsIcon from "@mui/icons-material/Settings";
import BugReportIcon from "@mui/icons-material/BugReport";
import ExtensionIcon from "@mui/icons-material/Extension";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import GavelIcon from "@mui/icons-material/Gavel";
import PolicyIcon from "@mui/icons-material/Policy";
import ViewQuiltIcon from "@mui/icons-material/ViewQuilt";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import TranslateIcon from "@mui/icons-material/Translate";
import { type MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { usePermissionContext } from "../../store/PermissionContext";
import { useBranding } from "../../branding/BrandingProvider";
import { DefaultBranding } from "../../branding/defaults";
import { isEnabled } from "../../feature-gate";

export const DRAWER_WIDTH = 260;

interface NavItem {
  label: MessageDescriptor;
  path: string;
  icon: React.ReactElement;
  /** Permission required to see this item. */
  permission: string;
  /**
   * Canonical dot-key release toggle (e.g. "admin.system"). When set, the nav
   * entry is ABSENT unless the toggle is enabled in the server-resolved set
   * (fail-closed, SPEC-FEATURE-TOGGLES-RULE-005). Omitted for entries that are
   * not release-gateable today — those stay visible behind RBAC only (no
   * regression vs. the legacy fail-open config).
   */
  feature?: string;
}

interface NavSection {
  title: MessageDescriptor;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    title: msg`Overview`,
    items: [
      {
        label: msg`Dashboard`,
        path: "/",
        icon: <DashboardIcon />,

        permission: "dashboard.read",
      },
      {
        label: msg`System Resources`,
        path: "/system",
        icon: <MonitorHeartIcon />,

        permission: "system.read",
      },
      {
        label: msg`Operations`,
        path: "/operations",
        icon: <SpeedIcon />,

        permission: "system.read",
      },
      {
        // `platform.admin`, matching the route (D-RLY-34): the backend keeps
        // `tenant.manage` + the platform-admin check on every Inference Health
        // route, `tenant.manage` is not in the frontend vocabulary, and
        // `platform.admin` is the string only platform_admin holds there. The
        // item follows the backend's audience, not the `system.read` gate of
        // the other system pages, so a principal granted `system.read` without
        // being a platform admin never sees an item whose reads all 403.
        label: msg`Inference Health`,
        path: "/inference-health",
        icon: <HealthAndSafetyIcon />,
        permission: "platform.admin",
      },
      {
        // The cost ledger, gated like its route (D-CST-01 A): the literal
        // `costs.view` from /me/permissions, so a per-user revoke hides it.
        // Release key admin.costs, the route's (SPEC-FEATURE-TOGGLES-RULE-008).
        label: msg`Costs`,
        path: "/costs",
        icon: <ReceiptLongIcon />,
        permission: "costs.view",
        feature: "admin.costs",
      },
      {
        label: msg`Storage`,
        path: "/storage",
        icon: <StorageIcon />,

        permission: "system.read",
      },
    ],
  },
  {
    title: msg`Management`,
    items: [
      {
        label: msg`Shared knowledge libraries`,
        path: "/knowledge-libraries",
        feature: "admin.knowledge-libraries",
        icon: <StorageIcon />,
        permission: "platform.admin",
      },
      {
        label: msg`Tenants`,
        path: "/tenants",
        icon: <BusinessIcon />,

        permission: "tenant.read",
      },
      {
        // Surface templates (SPEC-TENANT-SURFACES UI-002). Same release key as
        // the tenant Surfaces tab, so nav entry and route appear together and
        // stay ABSENT until the key is released (fail-closed, RULE-005).
        // `tenant.read` for the same reason the route uses it: `tenant.manage`
        // is not in FrontendPermissions, so gating on it would hide the entry
        // from every real platform admin (see apps/admin/routes.tsx).
        label: msg`Surface templates`,
        path: "/surface-templates",
        icon: <ViewQuiltIcon />,
        permission: "tenant.read",
        feature: "admin.tenants.surfaces",
      },
      {
        label: msg`Workspaces`,
        path: "/workspaces",
        icon: <WorkspacesIcon />,

        permission: "workspace.read",
      },
      {
        label: msg`Users`,
        path: "/users",
        icon: <PeopleIcon />,

        permission: "member.read",
      },
      {
        label: msg`Agents`,
        path: "/agents",
        icon: <SmartToyIcon />,

        permission: "agent.read",
      },
      {
        label: msg`User Search`,
        path: "/user-search",
        icon: <PersonSearchIcon />,

        permission: "member.read",
      },
      {
        label: msg`Registrations`,
        path: "/registrations",
        icon: <HowToRegIcon />,

        permission: "registration.read",
      },
      {
        label: msg`Invitations`,
        path: "/invitations",
        icon: <MailOutlineIcon />,
        permission: "member.read",
      },
    ],
  },
  {
    title: msg`Configuration`,
    items: [
      {
        label: msg`Providers`,
        path: "/providers",
        icon: <CloudIcon />,

        permission: "provider.read",
      },
      {
        label: msg`Models`,
        path: "/models",
        icon: <PsychologyIcon />,

        permission: "model.read",
      },
      {
        // LLM models (ADR inference-relay-and-local-engines §10.7; S5b): the
        // signed catalog and the internal engines, `model.read` to view, the
        // same gate as the route.
        label: msg`LLM models`,
        path: "/llm-models",
        icon: <MemoryIcon />,

        permission: "model.read",
        feature: "admin.llm-models",
      },
      {
        // Relay agents (ADR inference-relay-and-local-engines §9.1, §11.5;
        // S5): platform admin only (OD-1), the same `platform.admin` gate as
        // the route, so no one sees an item whose reads all 403 (D-RLY-34).
        label: msg`Relay agents`,
        path: "/relay-agents",
        icon: <HubIcon />,

        permission: "platform.admin",
        feature: "admin.relay-agents",
      },
      {
        label: msg`Feature Flags`,
        path: "/feature-flags",
        icon: <FlagIcon />,

        permission: "feature_flag.read",
      },
      {
        label: msg`Plugins`,
        path: "/plugins",
        icon: <ExtensionIcon />,

        permission: "settings.read",
      },
      {
        // Agentic research platform policy: the same `plugin.config.govern`
        // gate and the same fail-closed release key as the route.
        label: msg`Agentic Research`,
        path: "/agentic-research",
        icon: <TravelExploreIcon />,

        permission: "plugin.config.govern",
        feature: "admin.agentic-research",
      },
      {
        // Translator per-tenant prices: the same `plugin.config.govern` gate
        // and the same fail-closed release key as the route.
        label: msg`Translator`,
        path: "/translator",
        icon: <TranslateIcon />,

        permission: "plugin.config.govern",
        feature: "plugin.translator",
      },
      {
        label: msg`Crawler config`,
        path: "/crawler-config",
        icon: <SettingsApplicationsIcon />,

        permission: "settings.read",
        feature: "admin.crawlers",
      },
      {
        label: msg`Crawler approvals`,
        path: "/plugin-approvals",
        icon: <AssignmentTurnedInIcon />,

        permission: "settings.read",
      },
      {
        label: msg`Plugin Governance`,
        path: "/plugin-config-governance",
        icon: <PolicyIcon />,

        permission: "plugin.config.govern",
      },
    ],
  },
  {
    title: msg`Tools`,
    items: [
      {
        label: msg`API Keys`,
        path: "/api-keys",
        icon: <VpnKeyIcon />,

        permission: "api_key.read",
      },
      {
        label: msg`Chat Logs`,
        path: "/chat-logs",
        icon: <ChatIcon />,

        permission: "chat_log.read",
      },
      {
        label: msg`Audit Logs`,
        path: "/audit-logs",
        icon: <SecurityIcon />,

        permission: "audit.read",
      },
      {
        // `platform.admin` rather than a compliance-specific permission: the
        // page's read tier is open to ANY authenticated principal
        // (SPEC-COMPLIANCE-DELIVERY RULE-003), and per-tab gating on
        // compliance.admin happens inside the page — so no compliance
        // permission gates the whole entry. `platform.admin` is the frontend
        // permission every admin-SPA user already holds (FrontendPermissions,
        // internal/api/middleware/authz.go:1272), i.e. the same gate
        // RequireAdminAccess applies at apps/admin/App.tsx:148. It is the
        // honest way to say "visible to anyone who can reach this SPA" without
        // making NavItem.permission optional for one entry out of 28.
        // GavelIcon rather than PolicyIcon, which Plugin Governance already
        // uses in this same list.
        label: msg`Compliance`,
        path: "/compliance",
        icon: <GavelIcon />,
        permission: "platform.admin",
        // Same dark-slice toggle as the route (SPEC-FEATURE-TOGGLES RULE-005:
        // fail-closed, so the nav entry is ABSENT until the key is released).
        feature: "admin.compliance",
      },
      {
        label: msg`Permission Audit`,
        path: "/permission-audit",
        icon: <AdminPanelSettingsIcon />,

        permission: "audit.read",
      },
      {
        label: msg`A/B Testing`,
        path: "/experiments",
        icon: <ScienceIcon />,

        permission: "experiment.read",
      },
      {
        label: msg`Chat Playground`,
        path: "/playground",
        icon: <SmsIcon />,

        permission: "playground.read",
      },
    ],
  },
  {
    title: msg`System`,
    items: [
      {
        label: msg`Settings`,
        path: "/settings",
        icon: <SettingsIcon />,

        permission: "settings.read",
      },
      {
        label: msg`Debug`,
        path: "/debug",
        icon: <BugReportIcon />,
        permission: "dashboard.read",
      },
    ],
  },
];

export interface AdminSidebarProps {
  /** Whether the mobile drawer is open. */
  mobileOpen?: boolean;
  /** Callback to toggle mobile drawer. */
  onClose?: () => void;
}

export function AdminSidebar({
  mobileOpen = false,
  onClose,
}: AdminSidebarProps): React.ReactElement {
  const { hasPermission } = usePermissionContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLingui();
  /**
   * THE ADMIN SHELL'S OWN BRAND (UI-004, RULE-012). Both values come from the
   * ONE branding reader mounted at the SPA root: `product_name` is the title
   * and the logo's accessible name, `logo_url` is the image. This component
   * fetches nothing (SPEC-FRONTEND-RULE-077) and holds no vendor literal.
   *
   * FAIL-OPEN, THREE POSTURES (RULE-001, D-BR-15). The provider hands back
   * `DefaultBranding` while the bounded read is in flight and after it failed,
   * so both render the built-in brand silently. The third posture is a record
   * that WAS read and carries no logo (`logo_url: null`, CONTRACT-001), which
   * the context cannot cover: that is what the `defaults.ts` fallback is for -
   * a `null` src renders a broken image, and dropping the image leaves an empty
   * shell header, the RULE-042 degraded state a cosmetic failure must not reach.
   *
   * `web/src/branding/defaults.ts` is the ONE file allowed to hold the vendor
   * literal (RULE-012), so the built-in path is IMPORTED and never retyped.
   */
  const { branding } = useBranding();
  const productName = branding.product_name || DefaultBranding.product_name;
  const logoSrc = branding.logo_url ?? DefaultBranding.logo_url ?? undefined;

  const isActive = (path: string): boolean => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const isItemVisible = (item: NavItem): boolean => {
    // Release toggle (fail-closed) is orthogonal to RBAC and only applies when
    // the entry declares a canonical key; both must pass for the entry to show.
    if (item.feature !== undefined && !isEnabled(item.feature)) return false;
    return hasPermission(item.permission);
  };

  const visibleSections = NAV_SECTIONS
    .map((section) => ({
      ...section,
      items: section.items.filter(isItemVisible),
    }))
    .filter((section) => section.items.length > 0);

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Toolbar sx={{ gap: 1.5 }}>
        {/* The accessible name is the product name: the title beside it is the
            same word, so the image is named rather than described twice. */}
        <Box
          component="img"
          src={logoSrc}
          alt={productName}
          sx={{ height: 32, width: 32, objectFit: "contain", flexShrink: 0 }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" noWrap component="div" data-testid="sidebar-title">
            {productName}
          </Typography>
          {/* The app suffix stays a Lingui string while the product name beside
              it is not (D-BR-08). */}
          <Typography variant="caption" noWrap component="div" sx={{ color: "text.secondary" }}>
            <Trans>Admin</Trans>
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <Box data-testid="sidebar-nav" sx={{ overflowY: "auto", flex: 1 }}>
        {visibleSections.map((section, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <Divider sx={{ my: 0.5 }} />}
            <Typography
              variant="overline"
              sx={{
                px: 2,
                pt: idx === 0 ? 1.5 : 1,
                pb: 0.5,
                display: "block",
                color: "text.secondary",
                fontSize: "0.68rem",
                letterSpacing: "0.08em",
              }}
            >
              {t(section.title)}
            </Typography>
            <List dense disablePadding>
              {section.items.map((item) => (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    selected={isActive(item.path)}
                    onClick={() => {
                      navigate(item.path);
                      onClose?.();
                    }}
                    data-testid={`nav-${item.path.split("/").filter(Boolean).pop() ?? "dashboard"}`}
                    sx={{ py: 0.6, minHeight: 40 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={t(item.label)}
                      primaryTypographyProps={{
                        fontSize: "0.875rem",
                        fontWeight: isActive(item.path) ? 600 : 500,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: DRAWER_WIDTH,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: DRAWER_WIDTH,
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
}

export default AdminSidebar;
